import { FEATURE_FLAGS } from "@/lib/feature-flags";

/**
 * Which chassis a viewer gets while the UI uplift rolls out.
 *
 *   flag `off`     → always "classic"
 *   flag `enabled` → always "next"
 *   flag `preview` → "classic" unless the viewer opted in: `?ui=next` or `?ui=classic`
 *                    sets the choice and it is remembered in localStorage, so staff can
 *                    compare on UAT without keeping the param on every link.
 *
 * Migrated pages render their template version for "next" and the classic version
 * otherwise, until wave 2 ends. The shells expose the result as `data-ui` on their root.
 */
export type UiVariant = "classic" | "next";
export type UiUpliftFlag = "off" | "preview" | "enabled";

export const UI_VARIANT_STORAGE_KEY = "forge:ui-variant";

const isVariant = (value: string | null | undefined): value is UiVariant =>
  value === "classic" || value === "next";

interface VariantStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Record a `?ui=next` / `?ui=classic` choice, whatever the flag or route. Called from
 * main.tsx before the router mounts, so redirects that drop the query (e.g. `/` →
 * `/today`) can't lose it; the stored choice applies whenever the flag is `preview`.
 */
export function rememberUiChoice(search: string, storage: VariantStorage | null = browserStorage()): UiVariant | null {
  const param = new URLSearchParams(search).get("ui");
  if (!isVariant(param)) return null;
  try {
    storage?.setItem(UI_VARIANT_STORAGE_KEY, param);
  } catch {
    // Private mode or blocked storage: the param still applies to this page view.
  }
  return param;
}

/** Pure resolution; `storage` is written only when the URL carries a valid `ui` param. */
export function resolveUiVariant(
  flag: UiUpliftFlag,
  search: string,
  storage: VariantStorage | null,
): UiVariant {
  if (flag === "off") return "classic";
  if (flag === "enabled") return "next";

  const param = rememberUiChoice(search, storage);
  if (param) return param;

  try {
    const stored = storage?.getItem(UI_VARIANT_STORAGE_KEY);
    if (isVariant(stored)) return stored;
  } catch {
    // Unreadable storage falls back to the default.
  }
  return "classic";
}

function browserStorage(): VariantStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Resolve for the current page. */
export function getUiVariant(
  search: string = typeof window === "undefined" ? "" : window.location.search,
  flag: UiUpliftFlag = FEATURE_FLAGS.uiUplift,
): UiVariant {
  return resolveUiVariant(flag, search, browserStorage());
}
